<script setup lang="ts">
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { ref, watch } from 'vue';
import TimeTrackingService from '../../services/TimeTrackingService';
import { useToast } from 'primevue/usetoast';

interface TimeRecord {
  id: number;
  first_name?: string;
  last_name?: string;
  start_time?: string;
  end_time?: string;
}

const props = defineProps<{
  timeRecord?: TimeRecord;
}>();

const emit = defineEmits(['refreshTimes', 'update:visible']);

const toast = useToast();

const visible = defineModel<boolean>('visible');
const loading = ref(false);

const formData = ref<{
  startTime: Date | null;
  endTime: Date | null;
  firstName?: string;
  lastName?: string;
}>({
  startTime: null,
  endTime: null,
});

watch(() => props.timeRecord, (newValue) => {
  if (newValue) {
    formData.value.firstName = newValue.first_name;
    formData.value.lastName = newValue.last_name;
    formData.value.startTime = newValue.start_time ? new Date(newValue.start_time) : null;
    formData.value.endTime = newValue.end_time ? new Date(newValue.end_time) : null;
  }
}, { immediate: true });

const submitTimeRecord = async () => {
  if (!props.timeRecord?.id) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No time record selected', life: 3000 });
    return;
  }

  if (!formData.value.startTime) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Start time is required', life: 3000 });
    return;
  }

  if (!formData.value.endTime) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'End time is required', life: 3000 });
    return;
  }

  if (formData.value.endTime <= formData.value.startTime) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'End time must be after start time', life: 3000 });
    return;
  }

  try {
    loading.value = true;
    await TimeTrackingService.editTimeRecord(
      props.timeRecord.id,
      formData.value.startTime.toISOString(),
      formData.value.endTime.toISOString()
    );

    toast.add({ severity: 'success', summary: 'Success', detail: 'Time record updated', life: 3000 });
    emit('refreshTimes');
    visible.value = false;
  } catch (error: any) {
    const message = error.response?.data?.error?.message || 'Error updating time record';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  visible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    append-to="body"
    modal
    :breakpoints="{ '960px': '75vw', '640px': '96vw' }"
    :style="{ width: '40vw' }"
    :draggable="false"
    :resizable="false"
  >
    <template #header>
      <div class="flex flex-col gap-2">
        <h1 class="m-0 text-surface-900 dark:text-surface-0 font-semibold text-xl leading-normal">
          Edit Time Record
        </h1>
      </div>
    </template>

    <form
      class="flex flex-col gap-4 mt-4"
      @submit.prevent="submitTimeRecord"
    >
      <div>
        <label
          for="worker"
          class="block mb-1 text-color text-base"
        >Worker</label>
        <div class="font-bold text-surface-700 dark:text-surface-200">
          {{ formData.firstName }} {{ formData.lastName }}
        </div>
      </div>

      <div class="flex gap-4">
        <div class="w-full">
          <label
            for="start-time"
            class="block mb-1 text-color text-base"
          >Start Time</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-clock" />
            </InputGroupAddon>
            <DatePicker
              id="start-time"
              v-model="formData.startTime"
              show-time
              hour-format="24"
              fluid
            />
          </InputGroup>
        </div>
        <div class="w-full">
          <label
            for="end-time"
            class="block mb-1 text-color text-base"
          >End Time</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-clock" />
            </InputGroupAddon>
            <DatePicker
              id="end-time"
              v-model="formData.endTime"
              show-time
              hour-format="24"
              fluid
            />
          </InputGroup>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="w-full flex gap-4 justify-end border-t border-surface pt-8">
        <Button
          label="Cancel"
          text
          @click="handleClose"
        />
        <Button
          label="Save"
          rounded
          :loading="loading"
          @click="submitTimeRecord"
        />
      </div>
    </template>
  </Dialog>
</template>